import { useCallback, useEffect, useState } from "react";
import { DEFAULT_PAGE_SIZE, type ListParams, type PaginatedResult } from "@akhshy/types";
import { errorMessage } from "./errors.ts";

/** Minimal CRUD surface shared by every resource in the typed client. */
export interface ResourceClient<T, TInput, TUpdate> {
  list: (params?: ListParams) => Promise<PaginatedResult<T>>;
  create: (input: TInput) => Promise<T>;
  update: (id: string, patch: TUpdate) => Promise<T>;
  remove: (id: string) => Promise<void>;
}

export interface UseResource<T, TInput, TUpdate> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  setPage: (page: number) => void;
  refresh: () => Promise<void>;
  create: (input: TInput) => Promise<T>;
  update: (id: string, patch: TUpdate) => Promise<T>;
  remove: (id: string) => Promise<void>;
  /** Persist a new order for the current page (rewrites sortOrder). */
  reorder: (ordered: T[]) => Promise<void>;
}

/** Server-side paged CRUD hook: fetches ONE page at a time, incl. drafts. */
export function useResource<T, TInput, TUpdate>(
  client: ResourceClient<T, TInput, TUpdate>,
  pageSize: number = DEFAULT_PAGE_SIZE,
): UseResource<T, TInput, TUpdate> {
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const load = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      setError(null);
      try {
        const res = await client.list({
          page: targetPage,
          pageSize,
          includeUnpublished: true,
        });
        setItems(res.items);
        setTotal(res.total);
        setHasMore(res.hasMore);
        setPage(res.page);
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [client, pageSize],
  );

  useEffect(() => {
    void load(1);
  }, [load]);

  const refresh = useCallback(() => load(page), [load, page]);

  const create = useCallback(
    async (input: TInput) => {
      const created = await client.create(input);
      await load(page);
      return created;
    },
    [client, load, page],
  );

  const update = useCallback(
    async (id: string, patch: TUpdate) => {
      const updated = await client.update(id, patch);
      await load(page);
      return updated;
    },
    [client, load, page],
  );

  const remove = useCallback(
    async (id: string) => {
      await client.remove(id);
      // Step back a page if we just deleted the last row on a non-first page.
      const target = items.length === 1 && page > 1 ? page - 1 : page;
      await load(target);
    },
    [client, load, page, items.length],
  );

  const reorder = useCallback(
    async (ordered: T[]) => {
      // Optimistically show the new order, then persist a contiguous sortOrder
      // sequence (offset by the current page) for every row that moved.
      setItems(ordered);
      const base = (page - 1) * pageSize;
      const changed = ordered
        .map((item, i) => ({ item, sortOrder: base + i }))
        .filter(({ item, sortOrder }) => (item as { sortOrder: number }).sortOrder !== sortOrder);
      try {
        await Promise.all(
          changed.map(({ item, sortOrder }) =>
            client.update((item as { id: string }).id, { sortOrder } as unknown as TUpdate),
          ),
        );
      } finally {
        await load(page);
      }
    },
    [client, load, page, pageSize],
  );

  return {
    items,
    total,
    page,
    pageSize,
    totalPages,
    hasMore,
    loading,
    error,
    setPage: (p) => void load(p),
    refresh,
    create,
    update,
    remove,
    reorder,
  };
}
